/**
 * Cast VFX dispatcher — Slice D's client-side bus.
 *
 * Subscribes to five Colyseus broadcasts and spawns Three.js placeholder
 * shapes in the world so every cast is visible beyond the HUD:
 *
 *   - `castResolved`   → one-shot shape at the resolved target position
 *                         (or anchored to the target unit if provided).
 *   - `toggleStarted`  → persistent toggle-aura around the caster.
 *   - `toggleStopped`  → dispose the matching toggle-aura.
 *   - `channelStarted` → persistent channel-orb above the caster.
 *   - `channelStopped` → dispose the matching channel-orb.
 *
 * Shape selection: each ability row may set `placeholderVfx` to override the
 * targeting-mode default. If unset, the dispatcher falls back per mode (see
 * `defaultShapeFor`). The fallback ensures every Tier-9 spell that lands
 * later in the dev cycle gets *something* visible the moment its handler
 * activates — no row edit required.
 *
 * Lifecycle:
 *   - One-shot shapes are pushed into an array; the per-frame `tick(dtMs)`
 *     advances each and removes those whose `tick` returns false.
 *   - Persistent toggle/channel shapes are keyed by `(casterId, abilityId)`
 *     so a re-fire replaces the previous instance (re-pressing a toggle
 *     before its first start was acknowledged would otherwise double-up).
 *   - Persistent shapes also follow the caster's `object.position` each
 *     frame so they trail the player rather than sitting at the cast site.
 *
 * Server-side counterpart: `game/server/src/abilities/events.ts` plus the
 * call sites in `cast-flow.ts` / `toggle.ts` / `channel.ts`.
 */

import * as THREE from "three";
import type { Room } from "colyseus.js";
import {
  ABILITIES, type AbilityDef, type AbilityTargeting, type PlaceholderVfxSpec,
} from "@tribez/shared";
import {
  createSelfAura, createPointBurst, createUnitTag, createZoneDisc,
  createBeam, createChannelOrb, createToggleAura, type VfxInstance,
} from "./cast-vfx-shapes";

/**
 * A caster lookup the dispatcher uses to:
 *   - position toggle/channel persistent shapes each frame (caster follow).
 *   - anchor unit-tag one-shot shapes to the target's mesh at spawn time.
 *
 * `players` Map in main.ts already keys by sessionId. The dispatcher takes
 * a getter rather than the Map itself so creatures (different lookup) can
 * plug in later.
 */
export type GetCasterObject = (sessionId: string) => THREE.Object3D | undefined;

/** Terrain height sampler — point/zone shapes sit on the ground, so they need
 *  the y at the broadcast's (x, z). The `castResolved` broadcast carries only
 *  (x, z) (the server doesn't have a terrain sampler at hand); the client
 *  samples via `world.sampleHeight`. */
export type SampleHeight = (x: number, z: number) => number;

export interface CastVfxOptions {
  readonly scene: THREE.Scene;
  readonly room: Room;
  readonly getCasterObject: GetCasterObject;
  readonly sampleHeight: SampleHeight;
}

export interface CastVfxAPI {
  tick(dtMs: number): void;
  dispose(): void;
}

interface CastResolvedPayload {
  abilityId: string;
  casterId: string;
  x: number;
  z: number;
  targetUnitId?: string;
  at: number;
}

interface TogglePayload {
  casterId: string;
  abilityId: string;
  at: number;
}

interface ChannelPayload {
  casterId: string;
  abilityId: string;
  x: number;
  z: number;
  at: number;
}

interface ActiveVfx {
  readonly vfx: VfxInstance;
  /** Set for shapes that track a unit by sessionId — both persistent toggle/
   *  channel shapes and one-shot self-aura (Frost Armor etc. are buffs ON the
   *  caster, so the aura should move with them, not pin to cast coords). The
   *  per-frame tick re-anchors position to this caster's object. */
  readonly followCasterId?: string;
  /** Vertical offset for follow shapes (orb above head vs aura at feet). */
  readonly followYOffset?: number;
  /** Shape kind, mirrored from the spawning PlaceholderVfxSpec. Surfaced via
   *  the QA introspection handle so `probe.castVfxStats` can distinguish
   *  aura/tag/burst/etc without re-deriving from the Object3D. */
  readonly kind: PlaceholderVfxSpec["kind"];
  /** Color in the same hex format as the spec. QA-side reads only. */
  readonly color: number;
}

const PERSISTENT_KIND = new Set<PlaceholderVfxSpec["kind"]>(["channel-orb", "toggle-aura"]);

/**
 * Fallback shape per targeting mode. Used when an ability row omits
 * `placeholderVfx`. Default color is a neutral cyan so unstyled abilities
 * still surface visibly.
 */
function defaultShapeFor(targeting: AbilityTargeting): PlaceholderVfxSpec {
  switch (targeting.mode) {
    case "self":         return { kind: "self-aura", color: 0x88ccff, durationMs: 1500 };
    case "point":        return { kind: "point-burst", color: 0x88ccff, radius: 3, durationMs: 700 };
    case "unit":         return { kind: "unit-tag", color: 0x88ccff, durationMs: 1500 };
    case "point-charge": return { kind: "point-burst", color: 0x88ccff, radius: 3, durationMs: 700 };
    case "toggle":       return { kind: "toggle-aura", color: 0x88ccff };
    case "channel":      return { kind: "channel-orb", color: 0x88ccff };
  }
}

/** Factory by spec — dispatch on `kind`. Beam needs a `to` Vector3 supplied
 *  separately by the caller (broadcast carries x/z, caster pos is the from). */
function buildShape(
  spec: PlaceholderVfxSpec,
  casterPos: THREE.Vector3,
  targetPos: THREE.Vector3,
): VfxInstance {
  switch (spec.kind) {
    case "self-aura":
      return createSelfAura({ color: spec.color, durationMs: spec.durationMs, radius: spec.radius });
    case "point-burst":
      return createPointBurst({ color: spec.color, radius: spec.radius, durationMs: spec.durationMs });
    case "unit-tag":
      return createUnitTag({ color: spec.color, durationMs: spec.durationMs });
    case "zone-disc":
      return createZoneDisc({ color: spec.color, radius: spec.radius, durationMs: spec.durationMs });
    case "beam":
      return createBeam({
        color: spec.color, from: casterPos, to: targetPos, durationMs: spec.durationMs,
      });
    case "channel-orb":
      return createChannelOrb({ color: spec.color });
    case "toggle-aura":
      return createToggleAura({ color: spec.color });
  }
}

export function installCastVfx(opts: CastVfxOptions): CastVfxAPI {
  const { scene, room, getCasterObject, sampleHeight } = opts;

  // One-shot active effects — advanced each frame; auto-removed when tick
  // returns false. Persistent shapes go in `persistent` instead.
  const oneShots: ActiveVfx[] = [];

  // Persistent toggle/channel shapes keyed by `${casterId}:${abilityId}`. A
  // second start with the same key replaces the first (defensive; should be
  // rare given server-side de-dup).
  const persistent = new Map<string, ActiveVfx>();

  function persistentKey(casterId: string, abilityId: string): string {
    return `${casterId}:${abilityId}`;
  }

  function spawnOneShot(
    spec: PlaceholderVfxSpec,
    casterPos: THREE.Vector3,
    targetPos: THREE.Vector3,
    casterId: string,
  ): void {
    const vfx = buildShape(spec, casterPos, targetPos);
    // self-aura is anchored to the caster (Frost Armor / Stance / Backstab etc.
    // are buffs ON the player — the visual should follow them, not pin to the
    // ground at the cast site). beam positions itself in createBeam.
    // Everything else (point-burst, unit-tag, zone-disc) is a world-pinned
    // shape that anchors to the target position at spawn.
    let followCasterId: string | undefined;
    if (spec.kind === "self-aura") {
      vfx.object.position.copy(casterPos);
      followCasterId = casterId;
    } else if (spec.kind === "beam") {
      // Already positioned by createBeam.
    } else {
      vfx.object.position.copy(targetPos);
    }
    scene.add(vfx.object);
    oneShots.push({ vfx, followCasterId, kind: spec.kind, color: spec.color });
  }

  function spawnPersistent(
    spec: PlaceholderVfxSpec,
    casterId: string,
    abilityId: string,
    yOffset: number,
  ): void {
    const casterObj = getCasterObject(casterId);
    const casterPos = casterObj?.position ?? new THREE.Vector3();
    const vfx = buildShape(spec, casterPos, casterPos);
    vfx.object.position.copy(casterPos);
    vfx.object.position.y += yOffset;
    scene.add(vfx.object);

    const key = persistentKey(casterId, abilityId);
    // Replace any prior instance (defensive — server side dedupes).
    const prior = persistent.get(key);
    if (prior) {
      scene.remove(prior.vfx.object);
      prior.vfx.dispose();
    }
    persistent.set(key, { vfx, followCasterId: casterId, followYOffset: yOffset, kind: spec.kind, color: spec.color });
  }

  function despawnPersistent(casterId: string, abilityId: string): void {
    const key = persistentKey(casterId, abilityId);
    const entry = persistent.get(key);
    if (!entry) return;
    scene.remove(entry.vfx.object);
    entry.vfx.dispose();
    persistent.delete(key);
  }

  function resolvedShapeFor(def: AbilityDef): PlaceholderVfxSpec {
    return def.placeholderVfx ?? defaultShapeFor(def.targeting);
  }

  // -------------------------------------------------------------------------
  // Broadcast subscriptions
  // -------------------------------------------------------------------------

  room.onMessage("castResolved", (msg: CastResolvedPayload) => {
    const def = ABILITIES[msg.abilityId];
    if (!def) return;
    const spec = resolvedShapeFor(def);
    // Persistent toggle/channel shapes are spawned by their dedicated
    // started/stopped broadcasts — castResolved on the same cast also fires
    // (for the one-shot enable visual). Skip the persistent variants here.
    if (PERSISTENT_KIND.has(spec.kind)) return;

    const casterObj = getCasterObject(msg.casterId);
    const casterPos = casterObj?.position.clone() ?? new THREE.Vector3(msg.x, sampleHeight(msg.x, msg.z), msg.z);
    let targetPos: THREE.Vector3;
    if (msg.targetUnitId) {
      const targetObj = getCasterObject(msg.targetUnitId);
      targetPos = targetObj?.position.clone() ?? new THREE.Vector3(msg.x, sampleHeight(msg.x, msg.z), msg.z);
    } else {
      // Ground-anchored point: sample terrain height. Without this every ring
      // / disc / burst sinks under hilly terrain (player at y≈18 on this map).
      targetPos = new THREE.Vector3(msg.x, sampleHeight(msg.x, msg.z), msg.z);
    }
    spawnOneShot(spec, casterPos, targetPos, msg.casterId);
  });

  room.onMessage("toggleStarted", (msg: TogglePayload) => {
    const def = ABILITIES[msg.abilityId];
    if (!def) return;
    const spec = resolvedShapeFor(def);
    if (spec.kind !== "toggle-aura") return;
    spawnPersistent(spec, msg.casterId, msg.abilityId, 0.5);
  });

  room.onMessage("toggleStopped", (msg: TogglePayload) => {
    despawnPersistent(msg.casterId, msg.abilityId);
  });

  room.onMessage("channelStarted", (msg: ChannelPayload) => {
    const def = ABILITIES[msg.abilityId];
    if (!def) return;
    const spec = resolvedShapeFor(def);
    if (spec.kind !== "channel-orb") return;
    spawnPersistent(spec, msg.casterId, msg.abilityId, 3.2);
  });

  room.onMessage("channelStopped", (msg: ChannelPayload) => {
    despawnPersistent(msg.casterId, msg.abilityId);
  });

  // -------------------------------------------------------------------------
  // Per-frame tick
  // -------------------------------------------------------------------------

  function tick(dtMs: number): void {
    // Advance one-shots; drop expired in place. Re-anchor caster-following
    // ones (self-aura) BEFORE the tick so the visual moves with the player.
    for (let i = oneShots.length - 1; i >= 0; i--) {
      const entry = oneShots[i]!;
      if (entry.followCasterId) {
        const obj = getCasterObject(entry.followCasterId);
        if (obj) entry.vfx.object.position.copy(obj.position);
      }
      if (!entry.vfx.tick(dtMs)) {
        scene.remove(entry.vfx.object);
        entry.vfx.dispose();
        oneShots.splice(i, 1);
      }
    }
    // Persistent shapes: anchor position to caster, tick.
    persistent.forEach((entry) => {
      entry.vfx.tick(dtMs);
      if (entry.followCasterId) {
        const obj = getCasterObject(entry.followCasterId);
        if (obj) {
          entry.vfx.object.position.copy(obj.position);
          entry.vfx.object.position.y += entry.followYOffset ?? 0;
        }
      }
    });
  }

  function dispose(): void {
    for (const entry of oneShots) {
      scene.remove(entry.vfx.object);
      entry.vfx.dispose();
    }
    oneShots.length = 0;
    persistent.forEach((entry) => {
      scene.remove(entry.vfx.object);
      entry.vfx.dispose();
    });
    persistent.clear();
  }

  // QA introspection handle. Read-only snapshot of in-flight VFX state so
  // `game/qa/adapter.ts probe.castVfxStats` can assert aura position / follow
  // identity from a Playwright pass. Production code never reads this; tree-
  // shakable in prod build since adapter.ts is dev-only.
  (window as unknown as { __castVfx?: { snapshot: () => unknown } }).__castVfx = {
    snapshot: () => ({
      oneShots: oneShots.map((e) => ({
        kind: e.kind,
        color: e.color,
        position: { x: e.vfx.object.position.x, y: e.vfx.object.position.y, z: e.vfx.object.position.z },
        followCasterId: e.followCasterId,
      })),
      persistent: Array.from(persistent.entries()).map(([key, e]) => ({
        key,
        kind: e.kind,
        color: e.color,
        position: { x: e.vfx.object.position.x, y: e.vfx.object.position.y, z: e.vfx.object.position.z },
        followCasterId: e.followCasterId,
      })),
    }),
  };

  return { tick, dispose };
}
